import apiClient from "./api";
import {
  Credential,
  CredentialWithSecret,
  CreateCredentialRequest,
  CredentialListResponse,
  CredentialTotpCode,
  CredentialTotpConfig,
  ValidationResponse,
  UpdateCredentialTotpRequest,
  UpdateCredentialRequest,
  ShareCredentialRequest,
  ShareCredentialResponse,
  SharedCredentialMetadata,
  SharedCredential,
} from "@app-types/tap-partner-vault";

const CREDENTIALS_ENDPOINT = "/api/credentials";

export const credentialsService = {
  /**
   * List all credentials (metadata only)
   */
  async list(): Promise<CredentialListResponse> {
    const response = await apiClient.get<CredentialListResponse>(CREDENTIALS_ENDPOINT);
    return response.data;
  },

  /**
   * Get a single credential by ID (metadata only)
   */
  async get(id: string): Promise<Credential> {
    const response = await apiClient.get<Credential>(
      `${CREDENTIALS_ENDPOINT}/${id}`
    );
    return response.data;
  },

  /**
   * Create a new credential
   */
  async create(data: CreateCredentialRequest): Promise<Credential> {
    const response = await apiClient.post<Credential>(
      CREDENTIALS_ENDPOINT,
      data
    );
    return response.data;
  },

  /**
   * Delete a credential
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${CREDENTIALS_ENDPOINT}/${id}`);
  },

  /**
   * Update a credential secret
   */
  async update(id: string, data: UpdateCredentialRequest): Promise<void> {
    await apiClient.put(`${CREDENTIALS_ENDPOINT}/${id}`, data);
  },

  /**
   * Get the actual secret value for a credential
   */
  async getSecret(id: string): Promise<CredentialWithSecret> {
    const response = await apiClient.get<CredentialWithSecret>(
      `${CREDENTIALS_ENDPOINT}/${id}/secret`
    );
    return response.data;
  },

  /**
   * Get synced TOTP metadata for a credential
   */
  async getTotp(id: string): Promise<CredentialTotpConfig> {
    const response = await apiClient.get<CredentialTotpConfig>(
      `${CREDENTIALS_ENDPOINT}/${id}/totp`
    );
    return response.data;
  },

  /**
   * Create or update synced TOTP metadata for a credential
   */
  async upsertTotp(id: string, data: UpdateCredentialTotpRequest): Promise<CredentialTotpConfig> {
    const response = await apiClient.put<CredentialTotpConfig>(
      `${CREDENTIALS_ENDPOINT}/${id}/totp`,
      data
    );
    return response.data;
  },

  /**
   * Delete synced TOTP metadata for a credential
   */
  async deleteTotp(id: string): Promise<void> {
    await apiClient.delete(`${CREDENTIALS_ENDPOINT}/${id}/totp`);
  },

  /**
   * Fetch the current rotating TOTP code for a credential
   */
  async getTotpCode(id: string): Promise<CredentialTotpCode> {
    const response = await apiClient.get<CredentialTotpCode>(
      `${CREDENTIALS_ENDPOINT}/${id}/totp/code`
    );
    return response.data;
  },

  /**
   * Validate a credential
   */
  async validate(id: string): Promise<ValidationResponse> {
    const response = await apiClient.post<ValidationResponse>(
      `${CREDENTIALS_ENDPOINT}/${id}/validate`
    );
    return response.data;
  },

  /**
   * Create a share link for a credential
   */
  async share(id: string, data?: ShareCredentialRequest): Promise<ShareCredentialResponse> {
    const response = await apiClient.post<ShareCredentialResponse>(
      `${CREDENTIALS_ENDPOINT}/${id}/share`,
      data ?? {}
    );
    return response.data;
  },

  /**
   * Fetch shared credential metadata by token (public — no secret returned)
   */
  async getSharedMetadata(token: string): Promise<SharedCredentialMetadata> {
    const response = await fetch(`/api/shared/${token}`);
    if (response.status === 404) throw new Error("Share link not found");
    if (response.status === 410) throw new Error("This share link has expired");
    if (!response.ok) throw new Error("Failed to load shared credential");
    return response.json();
  },

  /**
   * Unlock a shared credential with the access code (public — returns secret)
   */
  async unlockShared(token: string, accessCode: string): Promise<SharedCredential> {
    const response = await fetch(`/api/shared/${token}/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_code: accessCode }),
    });
    if (response.status === 403) throw new Error("Invalid access code");
    if (response.status === 404) throw new Error("Share link not found");
    if (response.status === 410) throw new Error("This share link has expired");
    if (!response.ok) throw new Error("Failed to unlock shared credential");
    return response.json();
  },
};
